import { ModalSheet } from '@components/ModalSheet'
import { AppAlerts } from '@components/molecules/Alerts/AppAlerts'
import { DialogProvider, type DialogRegistry } from '@components/DialogProvider'
import { scopedTranslation } from '@frontend/utils/I18n'
import { CallingSessionSupportersDataTable } from '../components/CallingSessionSupportersDataTable'
import { InitiateCallingSessionDialog } from '../features/start-automated-calls/InitiateCallingSessionDialog'
import { SkipSupporterDialog } from '../features/skip-supporter/SkipSupporterDialog'
import { ImpactPanel } from '../components/calling-session-dashboard/ImpactPanel'
import type { CallingSessionSupportersIndexQueryType } from '@frontend/graphql/queries/callingSession'
import { hasSupporterId } from '../utils/sessionSupporters'
import { startDisabledReason, type StartCallingPermissions } from '../utils/startDisabledReason'

const t = scopedTranslation('views.calling_sessions.view_all_supporters')

const sheetDialogRegistry: DialogRegistry = {
  initiateCallingSession: InitiateCallingSessionDialog,
  skipSupporter: SkipSupporterDialog,
}

type CallingSessionSupportersType = CallingSessionSupportersIndexQueryType['callingSession']['sessionSupporters']

export function buildSessionSupportersData(
  sessionSupporters: CallingSessionSupportersType,
  nextSupporterToCallId?: string
) {
  return sessionSupporters.filter(hasSupporterId).map((sessionSupporter) => ({
    ...sessionSupporter,
    isUpNext: nextSupporterToCallId ? sessionSupporter.supporterId === nextSupporterToCallId : false,
  }))
}

type ViewAllSupportersSheetProps = {
  callingSession: CallingSessionSupportersIndexQueryType['callingSession']
  permissions?: StartCallingPermissions
}

function ViewAllSupportersSheet({ callingSession, permissions }: ViewAllSupportersSheetProps) {
  const nextSupporterToCallId = callingSession.nextAvailableSessionSupporterToCall?.supporter?.id
  const sessionSupporters = buildSessionSupportersData(
    callingSession.sessionSupporters,
    nextSupporterToCallId
  )

  return (
    <ModalSheet.Root reloadOnClose={['callingSession']}>
      <DialogProvider registry={sheetDialogRegistry}>
        <ModalSheet.Header title={t('title')} />
        <ModalSheet.Body>
          <AppAlerts alertsPath="viewAllSupporters" className="tw:mb-4" />

          <div className="tw:mb-5">
            <ImpactPanel
              callingSessionId={callingSession.id}
              callInProgressAt={callingSession.callInProgressAt ?? null}
              isPaused={callingSession.paused}
              totalCallsAttemptedCount={callingSession.totalCallsAttemptedCount}
              successfulContactsCount={callingSession.successfulContactsCount}
              unsuccessfulContactsCount={callingSession.unsuccessfulContactsCount}
              completedSupportersCount={callingSession.completedSupportersCount}
              availableSupportersThisCycleCount={callingSession.availableSupportersThisCycleCount}
              sessionProgress={callingSession.sessionProgress ?? { percentage: 0, completedCount: 0, totalSupportersCount: 0 }}
              currentCycle={callingSession.currentCycle ?? null}
              showViewAllButton={false}
              lastAttemptedSupporter={callingSession.lastAttemptedSessionSupporter?.supporter}
              startDisabledReason={startDisabledReason(permissions)}
            />
          </div>

          {sessionSupporters.length > 0 ? (
            <CallingSessionSupportersDataTable
              sessionSupporters={sessionSupporters}
              callingSessionId={callingSession.id}
              nextSupporterToCallId={nextSupporterToCallId}
            />
          ) : (
            <div className="tw:flex tw:items-center tw:justify-center tw:py-12">
              <div className="tw:text-gray-500">{t('empty_state')}</div>
            </div>
          )}
        </ModalSheet.Body>
      </DialogProvider>
    </ModalSheet.Root>
  )
}

export default ViewAllSupportersSheet
